import { useEffect, useRef, useState } from "react";
import { ArrowRight, Clock3, Globe2, ImageOff, ListChecks, RefreshCw } from "lucide-react";
import { api, getApiError } from "../api";
import { Button, InlineError, MarketplaceMark, PageHeader, StatePanel, formatDate } from "../components/ui";
import { useAsync } from "../hooks";
import type { PageContext } from "../App";
import type { AmazonConnection, WorkTask, WorkView } from "../types";

const VIEWS: Array<{ value: WorkView; label: string; hint: string; description: string; icon: typeof ListChecks }> = [
  { value: "now", label: "Jetzt erledigen", hint: "Deine nächsten Schritte", description: "Ein Eintrag pro Artikel und Land. Die wichtigsten Probleme stehen zuerst.", icon: ListChecks },
  { value: "waiting", label: "Wird automatisch geprüft", hint: "Nachprüfungen und laufende Vorgänge", description: "Hier musst du nichts erneut bestätigen. Fortbestehende Fehler kommen nach der Prüfung zurück.", icon: Clock3 },
  { value: "opportunities", label: "Möglichkeiten", hint: "Neue Länder und Empfehlungen", description: "Weitere Verkaufsländer und Empfehlungen aus lokalen Prüfungen.", icon: Globe2 },
];

export function HomePage({ navigate, notify, connection, connectionError, onConnectionReload }: PageContext & {
  connection: AmazonConnection | null;
  connectionError: string | null;
  onConnectionReload: () => Promise<void>;
}) {
  const [view] = useState<WorkView>(() => {
    const initial = new URLSearchParams(window.location.search).get("view");
    return initial === "waiting" || initial === "opportunities" ? initial : "now";
  });
  const queue = useAsync(() => api.tasks(view), [view]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const generation = useRef(0);
  useEffect(() => () => { generation.current += 1; }, []);
  const refreshQueue = async () => {
    generation.current += 1;
    setLoadingMore(false);
    await queue.reload();
  };
  const reloadRef = useRef(refreshQueue);
  reloadRef.current = refreshQueue;
  useEffect(() => {
    // Only reload stored findings; this never triggers a remote Amazon call.
    const refresh = () => { if (!document.hidden) void reloadRef.current(); };
    const timer = window.setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, []);
  const chooseView = (next: WorkView) => {
    generation.current += 1;
    navigate("home", next === "now" ? undefined : { view: next });
  };
  const loadMore = async () => {
    if (queue.data?.nextOffset == null || loadingMore || queue.loading) return;
    const requestGeneration = generation.current;
    setLoadingMore(true);
    try {
      const next = await api.tasks(view, queue.data.nextOffset);
      if (requestGeneration !== generation.current) return;
      queue.setData(current => current ? {
        ...next,
        items: [...current.items, ...next.items.filter(item => !current.items.some(existing => existing.id === item.id))],
      } : next);
    } catch (error) {
      if (requestGeneration === generation.current) notify(getApiError(error), "error");
    } finally {
      if (requestGeneration === generation.current) setLoadingMore(false);
    }
  };
  const sync = async () => {
    setSyncing(true);
    try {
      await api.sync();
      notify("Amazon-Abgleich gestartet. Den Fortschritt findest du unter „Wird automatisch geprüft“.", "info");
      if (view === "waiting") await refreshQueue(); else chooseView("waiting");
    } catch (error) { notify(getApiError(error), "error"); }
    finally { setSyncing(false); }
  };
  const openTask = (task: WorkTask) => {
    if (task.kind === "rollout") {
      navigate("listings", { sku: task.sellerSku, view: "catalog", rollout: "1" });
    } else {
      navigate("media", {
        tab: "actions", sku: task.sellerSku, marketplace: task.marketplaceCode,
        issue: task.issueId ?? "", from: "tasks", work: view,
      });
    }
  };
  const counts = queue.data?.summary;
  const nowCount = counts?.now;
  const connectionProblem = connectionError || (connection && connection.status !== "connected" ? connection.message || "Amazon-Verbindung prüfen." : null);
  return (
    <div className="work-page">
      <PageHeader title="Was ist jetzt zu tun?"
        description={nowCount == null ? "Deine Aufgaben werden ermittelt." : nowCount === 0
          ? "Im geladenen Stand gibt es keine offenen Entscheidungen für dich."
          : `${nowCount} ${nowCount === 1 ? "Aufgabe braucht" : "Aufgaben brauchen"} deine Aufmerksamkeit.`}
        actions={<Button variant="secondary" icon={<RefreshCw size={16} />} loading={queue.loading}
          onClick={() => void refreshQueue()}>Ansicht aktualisieren</Button>} />
      {connectionProblem && <div className="work-connection" role="status">
        <div><strong>Amazon-Verbindung prüfen</strong><p>{connectionProblem} Die Aufgaben zeigen den zuletzt gespeicherten Stand.</p></div>
        <Button variant="secondary" onClick={() => navigate("settings")}>Verbindung öffnen</Button>
        {connectionError && <Button variant="ghost" onClick={() => void onConnectionReload()}>Erneut prüfen</Button>}
      </div>}
      <div className="work-tabs" role="group" aria-label="Aufgabenansicht">
        {VIEWS.map(item => {
          const Icon = item.icon;
          return <button key={item.value} type="button" className={`work-tab work-tab--${item.value}`} aria-pressed={view === item.value}
            onClick={() => { if (item.value !== view) chooseView(item.value); }}>
            <span className="work-tab__icon" aria-hidden><Icon size={21} /></span>
            <span className="work-tab__copy">
              <span className="work-tab__label">{item.label}</span>
              <span className="work-tab__hint">{item.hint}</span>
            </span>
            <span className="work-tab__count">{counts ? counts[item.value] + (item.value === "waiting" ? queue.data?.backgroundJobCount ?? 0 : 0) : "–"}</span>
          </button>;
        })}
      </div>
      <p className="work-view-description">{VIEWS.find(item => item.value === view)?.description}</p>
      {queue.error && <InlineError message={`Aufgaben konnten nicht aktualisiert werden. ${queue.error}`} onRetry={() => void refreshQueue()} />}
      {queue.loading && !queue.data ? <StatePanel type="loading" title="Aufgaben werden geladen" /> : queue.data && <>
        {queue.data.items.length === 0 && !(view === "waiting" && queue.data.backgroundJobCount > 0) ? <StatePanel type="empty"
          title={queue.data.listingCount === 0 ? "Noch keine Angebote geladen" : view === "now" ? "Für dich ist gerade nichts offen" : view === "waiting" ? "Keine offenen Nachprüfungen" : "Keine weiteren Möglichkeiten erkannt"}
          description={queue.data.listingCount === 0 ? "Starte einen Amazon-Abgleich, damit der Manager deine Angebote prüfen kann."
            : view === "now" ? "Automatische Nachprüfungen und Empfehlungen stehen in den anderen Ansichten. Der Stand basiert auf den gespeicherten Amazon-Daten."
            : view === "waiting" ? "Bestätigte Preismaßnahmen erscheinen hier bis zur erneuten Amazon-Prüfung." : "Nach einem neuen Abgleich können weitere Hinweise erscheinen."}
          action={queue.data.listingCount === 0 ? <Button onClick={() => void sync()} loading={syncing}>Amazon abgleichen</Button> : undefined} />
          : <div className="work-list" aria-label={VIEWS.find(item => item.value === view)?.label}>
            {queue.data.items.map((task, index) => <article className={`work-task${view === "now" && task.priority >= 90 ? " work-task--urgent" : ""}${view === "waiting" ? " work-task--waiting" : ""}`} key={task.id}>
              <div className="work-task__image">{task.imageUrl ? <img src={task.imageUrl} alt="" loading="lazy" /> : <ImageOff size={24} />}</div>
              <div className="work-task__body">
                <div className="work-task__meta">
                  <span className={`work-task__label${view === "now" && task.priority >= 90 ? " work-priority" : ""}`}>{task.label}</span>
                  <MarketplaceMark code={task.marketplaceCode} decorative />
                  {task.fulfillmentMode && <span className={`work-fulfillment work-fulfillment--${task.fulfillmentMode.toLowerCase()}`}>{task.fulfillmentMode}</span>}
                </div>
                <h2>{view === "waiting" ? "Maßnahme wird nachgeprüft" : task.title}</h2>
                <p className="work-task__product">{task.productTitle} <span>· {task.sellerSku}</span></p>
                {view === "waiting" ? <p className="work-task__waiting"><Clock3 size={15} />
                  {task.retrying ? "Letzte Prüfung fehlgeschlagen. Erneuter Versuch" : "Nächste Prüfung"}{task.nextCheckAt ? ` ab ${formatDate(task.nextCheckAt)}` : ": vorgemerkt"}.
                </p> : <p className="work-task__next"><strong>Nächster Schritt:</strong> {task.nextStep}</p>}
                {task.issueCount > 1 && <span className="work-task__count">{task.issueCount} zugehörige Meldungen zusammengefasst</span>}
                {view === "waiting" && <details className="work-task__details"><summary>Worum geht es?</summary><p>{task.message}</p><p>Der nächste Prüflauf wird automatisch ausgeführt. Bei Verzögerungen bleibt der Vorgang hier sichtbar.</p></details>}
              </div>
              {view !== "waiting" && <Button variant={view === "now" && index === 0 ? "primary" : "secondary"}
                icon={<ArrowRight size={16} />} onClick={() => openTask(task)}>{task.kind === "rollout" ? "Länder auswählen" : task.actionLabel || "Aufgabe öffnen"}</Button>}
            </article>)}
          </div>}
        {queue.data.nextOffset != null && <Button variant="secondary" className="work-load-more" loading={loadingMore} disabled={queue.loading} onClick={() => void loadMore()}>Weitere Einträge laden ({queue.data.items.length} von {queue.data.total})</Button>}
        {view === "waiting" && queue.data.backgroundJobCount > 0 && <section className="work-background">
          <h2>Außerdem laufen {queue.data.backgroundJobCount} Hintergrundvorgänge</h2>
          {queue.data.backgroundJobs.map(job => <div key={job.id}><Clock3 size={15} /><span>{job.message || "Amazon-Daten werden verarbeitet"}{job.sellerSku ? ` · ${job.sellerSku}` : ""}</span><span>{job.status === "queued" ? "Wartet auf Start" : job.status === "retrying" ? "Erneuter Versuch" : "In Bearbeitung"}</span></div>)}
          <Button variant="ghost" onClick={() => navigate("jobs")}>Alle Vorgänge öffnen</Button>
        </section>}
      </>}
      <footer className="work-footer"><span><ListChecks size={15} /> Stand der Qualitätsprüfung: {formatDate(queue.data?.lastScanAt ?? undefined)}{queue.data?.lastScanStatus && queue.data.lastScanStatus !== "succeeded" ? " · noch nicht erfolgreich abgeschlossen" : ""}</span>
        <details className="work-tools"><summary>Abgleich und weitere Werkzeuge</summary><div>
          <Button variant="secondary" onClick={() => void sync()} loading={syncing}>Amazon abgleichen</Button>
          <Button variant="ghost" onClick={() => navigate("uploader")}>Neuen Artikel anlegen</Button>
          <Button variant="ghost" onClick={() => navigate("media")}>Alle Problemmeldungen</Button>
          <Button variant="ghost" onClick={() => navigate("jobs")}>Verlauf und Prüfungen</Button>
        </div></details>
      </footer>
    </div>
  );
}
